/**
 * BroadcastComposer — /messages/broadcast
 *
 * MV5 Sprint 2.C. Instructor-only broadcast composer with the required
 * "Send test to me first" affordance before the irreversible fan-out.
 *
 * Flow:
 *   1. Compose subject + body + audience selector
 *   2. Click "Send test to me first" → POST /instructor/announcements with
 *      send_test_to_me_first=true → drawer confirms it landed in the
 *      instructor's own inbox
 *   3. Click "Send to N recipients" → POST /instructor/announcements with
 *      the real audience → confirmation dialog → fan-out
 *
 * Audience selector shapes (recipients_query):
 *   { kind: 'all' }
 *   { kind: 'vertical', targetId: '<vertical-uuid>' }
 *   { kind: 'region', targetId: 'NZ' | 'PK' | ... }
 *   { kind: 'cohort', targetId: '<cohort-uuid>' }
 */

import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionManagerApi } from '../services/apiClient';
import Dialog from '../components/Dialog';
import { DraftRequestKey } from '../lib/requestIdentity';
import { requireArray, requireRecord } from '../services/responseValidation';

type AudienceKind = 'all' | 'vertical' | 'region' | 'cohort';

interface AuditRow {
  id: string;
  subject: string;
  sentAt: string | null;
  recipientCount: number;
}

const BroadcastComposer: React.FC = () => {
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [audienceKind, setAudienceKind] = useState<AudienceKind>('all');
  const [targetId, setTargetId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [status, setStatus] = useState<{
    kind: 'idle' | 'test-sent' | 'sent' | 'error';
    message?: string;
    recipientCount?: number;
  }>({ kind: 'idle' });
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [auditError, setAuditError] = useState(false);
  const [testedContent, setTestedContent] = useState<string | null>(null);
  const requestKeys = useRef({
    test: new DraftRequestKey(),
    broadcast: new DraftRequestKey(),
  });

  const recipientsQuery = useMemo(() => {
    if (audienceKind === 'all') return { kind: 'all' as const };
    return { kind: audienceKind, targetId };
  }, [audienceKind, targetId]);
  const contentIdentity = JSON.stringify([subject, body, recipientsQuery]);
  const testedCurrentContent = testedContent === contentIdentity;

  const canSubmit =
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    (audienceKind === 'all' || targetId.length > 0) &&
    !submitting;

  const loadAudit = async () => {
    try {
      const resp = await sessionManagerApi.get(
        '/instructor/announcements/audit',
      );
      setAudit(requireArray<AuditRow>(resp.data?.audit, 'broadcast history'));
      setAuditError(false);
    } catch (err) {
      setAuditError(true);
    }
  };

  React.useEffect(() => {
    void loadAudit();
  }, []);

  const send = async (testFirst: boolean) => {
    if (!canSubmit || (!testFirst && !testedCurrentContent)) return;
    const draftKey = testFirst
      ? requestKeys.current.test
      : requestKeys.current.broadcast;
    const requestId = draftKey.forPayload(
      JSON.stringify([subject.trim(), body.trim(), recipientsQuery, testFirst]),
    );
    setSubmitting(true);
    setStatus({ kind: 'idle' });
    try {
      const resp = await sessionManagerApi.post('/instructor/announcements', {
        subject: subject.trim(),
        message_body: body.trim(),
        recipients_query: recipientsQuery,
        send_test_to_me_first: testFirst,
        client_request_id: requestId,
      });
      const result = requireRecord(resp.data, 'broadcast delivery');
      if (
        !Number.isInteger(result.recipient_count) ||
        result.recipient_count < 0
      )
        throw new Error(
          'The delivery response is incomplete. Retry to check the saved result.',
        );
      draftKey.complete(requestId);
      if (testFirst) {
        setTestedContent(contentIdentity);
        setStatus({
          kind: 'test-sent',
          message:
            'Test delivered to your inbox. Check it — then click "Send to recipients" to fan out.',
        });
      } else {
        setTestedContent(null);
        setStatus({
          kind: 'sent',
          message: `Broadcast sent to ${resp.data.recipient_count} learners.`,
          recipientCount: resp.data.recipient_count,
        });
        setSubject('');
        setBody('');
        await loadAudit();
      }
    } catch (err: any) {
      setStatus({
        kind: 'error',
        message:
          err?.response?.data?.message ||
          err?.message ||
          'Broadcast failed. Please try again.',
      });
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div style={{ maxWidth: 780, padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Broadcast composer</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--ink-muted, #6b6b6b)' }}>
            Send an announcement to cohorts, verticals, regions, or the whole
            learner base. Always send a test to yourself first.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/messages')}
          style={btn('secondary')}
        >
          ← Back to inbox
        </button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit || !testedCurrentContent) return;
          if (audienceKind === 'all') setConfirmOpen(true);
          else void send(false);
        }}
        style={{ marginTop: 24, display: 'grid', gap: 16 }}
      >
        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontWeight: 600 }}>Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            style={input()}
            placeholder="e.g. Week 6 kickoff — new lab drop"
            required
            maxLength={255}
          />
        </label>

        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontWeight: 600 }}>Message body</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            style={{ ...input(), fontFamily: 'inherit' }}
            required
            maxLength={10000}
          />
        </label>

        <fieldset
          style={{ border: '1px solid #d0d0d0', borderRadius: 6, padding: 12 }}
        >
          <legend style={{ fontWeight: 600, padding: '0 4px' }}>
            Audience
          </legend>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {(['all', 'vertical', 'region', 'cohort'] as AudienceKind[]).map(
              (k) => (
                <label
                  key={k}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <input
                    type="radio"
                    name="audience"
                    checked={audienceKind === k}
                    onChange={() => setAudienceKind(k)}
                  />
                  {k === 'all' ? 'Everyone' : k}
                </label>
              ),
            )}
          </div>
          {audienceKind !== 'all' && (
            <input
              type="text"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              placeholder={
                audienceKind === 'region'
                  ? 'Region code (NZ, PK, US, ...)'
                  : `${audienceKind} ID`
              }
              style={{ ...input(), marginTop: 12 }}
              required
            />
          )}
        </fieldset>

        {status.kind !== 'idle' && (
          <div
            role={status.kind === 'error' ? 'alert' : 'status'}
            style={{
              padding: 12,
              borderRadius: 6,
              background:
                status.kind === 'error'
                  ? '#fef2f2'
                  : status.kind === 'sent'
                    ? '#f0fdf4'
                    : '#fef9c3',
              color:
                status.kind === 'error'
                  ? '#7f1d1d'
                  : status.kind === 'sent'
                    ? '#166534'
                    : '#78350f',
            }}
          >
            {status.message}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void send(true)}
            style={btn('secondary')}
          >
            {submitting ? 'Sending…' : 'Send test to me first'}
          </button>
          <button
            type="submit"
            disabled={!canSubmit || !testedCurrentContent}
            style={btn('primary')}
            title={
              !testedCurrentContent
                ? 'Send a test to yourself first (irreversible action)'
                : 'Fan out to all resolved recipients'
            }
          >
            Send to recipients
          </button>
        </div>
      </form>

      {confirmOpen && audienceKind === 'all' && (
        <div style={confirmModal()}>
          <Dialog
            label="Confirm broadcast delivery"
            onClose={() => {
              if (!submitting) setConfirmOpen(false);
            }}
            style={confirmModalInner()}
          >
            <h2 style={{ marginTop: 0 }}>Send to everyone?</h2>
            <p>
              This will deliver the announcement to every active learner account
              within your teaching scope. This action is irreversible.
            </p>
            <div
              style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}
            >
              <button
                onClick={() => setConfirmOpen(false)}
                style={btn('secondary')}
              >
                Cancel
              </button>
              <button onClick={() => void send(false)} style={btn('primary')}>
                Yes, send now
              </button>
            </div>
          </Dialog>
        </div>
      )}

      <section style={{ marginTop: 40 }}>
        <h2>Recent broadcasts</h2>
        {auditError ? (
          <div role="alert">
            <p>Couldn&apos;t load broadcast delivery records.</p>
            <button type="button" onClick={() => loadAudit()}>
              Retry
            </button>
          </div>
        ) : audit.length === 0 ? (
          <p style={{ color: 'var(--ink-muted, #6b6b6b)' }}>
            No broadcasts yet. Your first send will land here.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th()}>Subject</th>
                <th style={th()}>Sent</th>
                <th style={th()}>Recipients</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id}>
                  <td style={td()}>{a.subject}</td>
                  <td style={td()}>
                    {a.sentAt ? new Date(a.sentAt).toLocaleString() : '—'}
                  </td>
                  <td style={td()}>{a.recipientCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default BroadcastComposer;

// ── style helpers (inline; kept out of a .module.css because the sprint
//    priority was breadth over per-page polish; the styles live here and
//    can be lifted into a module later)

function input(): React.CSSProperties {
  return {
    border: '1px solid #d0d0d0',
    borderRadius: 6,
    padding: '8px 10px',
    fontSize: 14,
    width: '100%',
    boxSizing: 'border-box',
  };
}
function btn(kind: 'primary' | 'secondary'): React.CSSProperties {
  return {
    padding: '8px 14px',
    borderRadius: 6,
    border: kind === 'primary' ? 'none' : '1px solid #d0d0d0',
    background: kind === 'primary' ? '#6b4fea' : 'transparent',
    color: kind === 'primary' ? '#fff' : '#1a1a1a',
    fontWeight: 600,
    cursor: 'pointer',
  };
}
function th(): React.CSSProperties {
  return {
    textAlign: 'left',
    padding: '8px 0',
    borderBottom: '1px solid #e0e0e0',
  };
}
function td(): React.CSSProperties {
  return { padding: '8px 0', borderBottom: '1px solid #f0f0f0' };
}
function confirmModal(): React.CSSProperties {
  return {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  };
}
function confirmModalInner(): React.CSSProperties {
  return {
    background: '#fff',
    padding: 24,
    borderRadius: 8,
    maxWidth: 480,
    width: '90%',
  };
}
